import { Suspense, useEffect, useState } from 'react';
import { Link, Redirect, Route, Switch, useLocation } from 'wouter';
import { Layout } from './components/Layout';
import { SettingsShell } from './components/SettingsShell';
import {
  ChunkErrorPanel,
  LazyChunkErrorBoundary,
} from './components/LazyChunkErrorBoundary';
import { RedirectFromRoot } from './components/RedirectFromRoot';
import { ToolsLoginRedirect } from './components/ToolsLoginRedirect';
import { redirectToToolsLogout } from './lib/tools-login';
import { ToastProvider } from './components/Toast';
import { OnboardingTour } from './components/OnboardingTour';
import { Spinner } from './components/ui/Spinner';
import { api, ApiError, apiTryGet } from './lib/api';
import { type AuthedUser, deriveAuthedUser, type Me } from './lib/auth-types';
import { AUTH_USER_CHANGED } from './lib/events';
import { lazyPageFactories } from './lib/lazyPages';
import { lazyWithRetry } from './lib/lazyWithRetry';
import { schedulePreloads } from './lib/preloadRoutes';
// Eager: häufigste Pages (Default-Route landing + Asset-Browser).
import { DashboardPage } from './pages/DashboardPage';
import { LibraryPage } from './pages/LibraryPage';

// Lazy pages: import-factories leben in lib/lazyPages.ts (single source of truth
// — geteilt mit preloadRoutes.ts, kein dual-string-source). lazyWithRetry
// wrappt React.lazy mit auto-retry-once gegen transiente chunk-load-failures.
const AssetDetailPage = lazyWithRetry(lazyPageFactories.AssetDetailPage);
const TrashPage = lazyWithRetry(lazyPageFactories.TrashPage);
const FavoritesPage = lazyWithRetry(lazyPageFactories.FavoritesPage);
const InsightsPage = lazyWithRetry(lazyPageFactories.InsightsPage);
const CollectionListPage = lazyWithRetry(lazyPageFactories.CollectionListPage);
const CollectionDetailPage = lazyWithRetry(lazyPageFactories.CollectionDetailPage);
const RecipeListPage = lazyWithRetry(lazyPageFactories.RecipeListPage);
const RecipeDetailPage = lazyWithRetry(lazyPageFactories.RecipeDetailPage);
const SharesListPage = lazyWithRetry(lazyPageFactories.SharesListPage);
const PublicLinksPage = lazyWithRetry(lazyPageFactories.PublicLinksPage);
const BrandPage = lazyWithRetry(lazyPageFactories.BrandPage);
const BrandAdminPage = lazyWithRetry(lazyPageFactories.BrandAdminPage);
// ProfilePage/TeamPage/VocabAdminPage werden jetzt im SettingsShell lazy
// gerendert (Unified Settings-IA, /settings-Subtree) — nicht mehr hier.

// Sprint C3a: Route-Guard helper für admin-only Routes. Renders nichts und
// redirected via useEffect zu `/` (Dashboard). Defensive: wenn nicht-admin
// direkt `/settings/team` URL aufruft, gibt's keinen "Forbidden"-Screen
// (würde Information über die Existenz der Page leaken) — stattdessen
// silent redirect.
function ForbiddenRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation('/', { replace: true });
  }, [setLocation]);
  return null;
}

// Branded Loading-Indikator (Sprint UI-D1) — ersetzt die alten nackten
// Mono-Text-Stubs. `fullscreen`: pre-Layout-States (auth-check, Login-Suspense).
// In-flow (default): Page-Suspense innerhalb des Layout-<main>.
function LoadingScreen({ fullscreen = false }: { fullscreen?: boolean }) {
  return (
    <div
      className={
        fullscreen
          ? 'min-h-screen flex items-center justify-center bg-brand-cream'
          : 'min-h-[200px] flex items-center justify-center'
      }
    >
      <Spinner size="lg" className="text-brand-green-700" />
      <span className="sr-only">Lädt…</span>
    </div>
  );
}

// Me-wire-type + AuthedUser-derived type leben in lib/auth-types.ts.
// `Me` bleibt der server-side wire-type (id/email/displayName/role).
// `AuthedUser = Me & {isAdmin}` ist frontend-local — wir reichen den
// DERIVED type an Layout/Sidebar/Pages durch (Sprint C3a A2).

type AuthState =
  | { status: 'checking' }
  | { status: 'unauthed' }
  | { status: 'authed'; user: AuthedUser };

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'checking' });
  // location als resetKey für LazyChunkErrorBoundary — bei Route-Change nach
  // chunk-error reset der Boundary, sodass die neue Route wieder versucht
  // werden kann (statt stuck auf ChunkErrorPanel zu bleiben).
  const [location] = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await apiTryGet<Me>('/api/auth/me');
      if (cancelled) return;
      setAuth(
        me
          ? { status: 'authed', user: deriveAuthedUser(me) }
          : { status: 'unauthed' },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Idle-Preload für AssetDetail + CollectionList nach Auth-Success.
  // Trigger NACH authed-Branch (nicht in checking/unauthed) damit der Preload
  // nicht mit Login-Bootstrap konkurriert. Pattern: memory/preload-patterns.md.
  useEffect(() => {
    if (auth.status !== 'authed') return;
    const cancel = schedulePreloads();
    return cancel;
  }, [auth.status]);

  // AUTH_USER_CHANGED listener — fires from ProfilePage after a successful
  // PATCH /api/auth/me. Refetch the canonical /me row and replace auth.user
  // so Sidebar-Footer + Avatar-Initials update without a page reload.
  //
  // AbortController-coalescing analog Sidebar's TRASH_CHANGED handler:
  // if multiple events arrive in quick succession (rapid-fire saves) we
  // abort any pending refetch so only the latest response wins.
  useEffect(() => {
    if (auth.status !== 'authed') return;
    let active: AbortController | null = null;
    const handler = (): void => {
      active?.abort();
      const ctl = new AbortController();
      active = ctl;
      void (async () => {
        try {
          const me = await api<Me>('/api/auth/me', { signal: ctl.signal });
          if (ctl.signal.aborted) return;
          setAuth({ status: 'authed', user: deriveAuthedUser(me) });
        } catch (err) {
          if ((err as { name?: string })?.name === 'AbortError') return;
          // 401 — session was invalidated mid-edit. Drop to unauthed; the
          // login screen will pick up. Other errors: log + leave UI alone.
          if (err instanceof ApiError && err.status === 401) {
            setAuth({ status: 'unauthed' });
            return;
          }
          console.error('[auth] AUTH_USER_CHANGED refetch failed:', err);
        }
      })();
    };
    window.addEventListener(AUTH_USER_CHANGED, handler);
    return () => {
      window.removeEventListener(AUTH_USER_CHANGED, handler);
      active?.abort();
    };
  }, [auth.status]);

  if (auth.status === 'checking') {
    return <LoadingScreen fullscreen />;
  }

  // P1c PR-C (Must 10): DAM hat keinen eigenen Login mehr — angemeldet wird auf
  // tools.greenforce.com. `app='dam'` als Literal, damit der Handoff nach dem
  // Login zielgenau hierher zurückkommt (inkl. Deep-Link via `next`).
  if (auth.status === 'unauthed') {
    return <ToolsLoginRedirect app="dam" />;
  }

  // P1c PR-C: „Abmelden" ist ab jetzt ein GLOBALER Logout. Nur das lokale Cookie
  // zu löschen würde nichts bewirken — der unauthed-Branch schickt den User nach
  // tools, wo die IdP-Session noch lebt und laut PR-A sofort einen Auto-Handoff
  // zurück mintet. Deshalb: lokal abmelden, dann nach `tools/logout`, wo die
  // Session an der Wurzel stirbt. KEIN `setAuth('unauthed')` mehr — das würde den
  // Login-Redirect (mit `app`/`next`) auslösen und damit genau den Rundflug, den
  // wir verhindern wollen.
  const handleLogout = async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (err) {
      console.error('[auth] logout failed:', err);
    }
    redirectToToolsLogout();
  };

  // Forced-Change-Gate (P1c PR-C, Must 11 / D-P1c-7a): der Wechsel selbst passiert
  // ab jetzt auf tools — `POST /api/auth/change-password` existiert auf DAM nicht
  // mehr, ein hier gerendertes Formular liefe ins 404. Also derselbe Redirect wie
  // im unauthed-Fall: der tv-Bump beim Wechsel killt ohnehin alle Alt-Sessions,
  // und `app`+`next` bringen den User anschliessend genau hierher zurück.
  if (auth.user.mustChangePassword) {
    return <ToolsLoginRedirect app="dam" />;
  }

  // Outer-Switch: `/` redirects OUTSIDE Layout-Wrapper (kein Shell-Flash).
  // Alle anderen Pfade fallen in den Layout-Wrapper. Pattern aus
  // tasks/spec.md Item 9 (Slice-4 Default-Route-Migration).
  // Inner-Suspense INSIDE Layout: Sidebar+Topbar bleiben sichtbar bei
  // lazy-Page-Loads, nur der Page-Container zeigt den Lade-Spinner.
  return (
    <ToastProvider>
      {/* First-Run-Orientierungs-Tour: triggert genau 1× wenn onboarded_at NULL.
          Rendert null; reagiert auf auth.user (inkl. onboardedAt aus /me). */}
      <OnboardingTour me={auth.user} />
      <Switch>
        <Route path="/">
          <RedirectFromRoot />
        </Route>
        <Route>
          <Layout user={auth.user} onLogout={handleLogout}>
            <LazyChunkErrorBoundary
              fallback={<ChunkErrorPanel />}
              resetKey={location}
            >
              <Suspense fallback={<LoadingScreen />}>
                <Switch>
                  <Route path="/dashboard" component={DashboardPage} />
                  <Route path="/library" component={LibraryPage} />
                  <Route path="/favorites" component={FavoritesPage} />
                  <Route path="/assets/:id">
                    {(params) => <AssetDetailPage id={params.id} />}
                  </Route>
                  {/* /upload-Route entfernt 2026-06-09: Upload ist ein globales
                      Modal (Layout-Ebene), kein eigener Screen mehr. */}
                  {/* Tag-Pflege lebt seit 2026-06-09 ausschließlich im Vokabular
                      (/settings/vocab → TagManager mit Merge); separate
                      /tags-Seite entfernt. */}
                  {/* Alte /profile-URL → /settings/profile (Bookmark-Preserve).
                      Profil lebt seit der Unified Settings-IA (2026-06-09) als
                      Tab im /settings-Shell. */}
                  <Route path="/profile">
                    <Redirect to="/settings/profile" replace />
                  </Route>
                  <Route path="/trash" component={TrashPage} />
                  <Route path="/shares" component={SharesListPage} />
                  {/* Händler-Links (Spec Phase 1): Verwaltungsliste der
                      permanenten öffentlichen Bild-URLs. Lesen alle Rollen,
                      Depublish-Buttons admin-gated (Server-RBAC bleibt SSOT). */}
                  <Route path="/public-links">
                    <PublicLinksPage authedUser={auth.user} />
                  </Route>
                  {/* Unified Settings-Shell: ein /settings-Subtree mit Sub-Nav
                      (Profil · Team · Vokabular). Der Shell parst die aktive Tab
                      aus useLocation, redirectet Index/Unknown → /settings/profile
                      und gated Team/Vokabular (admin-only) → / (ForbiddenRedirect-
                      Parität, jetzt im Shell statt pro Route). */}
                  <Route path="/settings/:tab?">
                    <SettingsShell authedUser={auth.user} />
                  </Route>
                  <Route path="/insights" component={InsightsPage} />
                  {/* Sprint C5 (2026-05-18): Brand Center.
                      /brand        — Read-only Hub für alle auth-User
                      /brand/admin  — Admin-only Curation (Upload/Edit/Delete).
                      Server-RBAC bleibt SSOT; Frontend-route-guard via
                      hasMinRole im BrandAdminPage redirected non-admins. */}
                  <Route path="/brand">
                    <BrandPage authedUser={auth.user} />
                  </Route>
                  <Route path="/brand/admin">
                    {auth.user.isAdmin ? (
                      <BrandAdminPage authedUser={auth.user} />
                    ) : (
                      <ForbiddenRedirect />
                    )}
                  </Route>
                  <Route path="/collections" component={CollectionListPage} />
                  <Route path="/collections/:id">
                    {(params) => <CollectionDetailPage id={params.id} />}
                  </Route>
                  <Route path="/recipes" component={RecipeListPage} />
                  <Route path="/recipes/:id">
                    {(params) => <RecipeDetailPage id={params.id} />}
                  </Route>
                  <Route>
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
                      <h1 className="font-display text-6xl text-brand-green-700">
                        404
                      </h1>
                      <p className="text-sm text-brand-green-600">
                        Diese Seite gibt es nicht.
                      </p>
                      <Link
                        href="/library"
                        className="mt-2 text-sm font-medium text-brand-green-700 underline underline-offset-2 hover:text-brand-green-800"
                      >
                        Zur Bibliothek
                      </Link>
                    </div>
                  </Route>
                </Switch>
              </Suspense>
            </LazyChunkErrorBoundary>
          </Layout>
        </Route>
      </Switch>
    </ToastProvider>
  );
}
